import React, { FunctionComponent } from 'react';
import { useMedia } from 'react-media-match';
import PhotoSwipe from 'react-photoswipe-2';
import { FragmentConfiguration } from '@/config';
import { MediaAsset } from '@/src/interfaces';
import StyledPhotoSwipe from '../styledPhotoSwipe';
import { useGalleryContext } from '../GalleryContext';

export const handlePswpChange = ({
  instance,
  setActiveImage,
  getCurrentSlider,
  isTestMode = false, // for testing only
}): void => {
  let currentIndex = 0;
  if (!isTestMode) {
    currentIndex = instance.getCurrentIndex();
    getCurrentSlider().slickGoTo(currentIndex);
  }
  setActiveImage(currentIndex);
};

interface GalleryZoomOverlayProps {
  /** The images to render in the zoom overlay */
  images: MediaAsset[];
  /** The fragment configuration to determine the correct image format. */
  config: FragmentConfiguration;
  /** The current device string. */
  device: string;
}

export const GalleryZoomOverlay: FunctionComponent<GalleryZoomOverlayProps> = ({
  images,
  config,
  device,
}): JSX.Element => {
  const {
    setActiveImage,
    getActiveImage,
    getCurrentSlider,
    getIsZoom,
    setIsZoom,
  } = useGalleryContext();
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });

  /** Setup Zoom Properties */
  const items = [];
  const zoomImageWidth = config.galleryZoomImage[device].width;
  const zoomImageHeight = config.galleryZoomImage[device].height;
  const zoomImageFormat = config.galleryZoomImage[device].formatName;

  if (images && images.length > 0) {
    // eslint-disable-next-line array-callback-return
    images.map((image: MediaAsset) => {
      const imageData = {
        h: zoomImageHeight,
        src: image.type === 'IMAGE' ? image.url.concat(`?$${zoomImageFormat}$`) : image.url,
        w: zoomImageWidth,
      };
      if (image.type === 'IMAGE') {
        items.push(imageData);
      }
    });
  }

  return (
    <StyledPhotoSwipe data-testid="zoom-modal" isMobile={isMobile}>
      <PhotoSwipe
        afterChange={(instance): void =>
          handlePswpChange({
            getCurrentSlider,
            instance,
            setActiveImage,
          })
        }
        isOpen={getIsZoom()}
        items={items}
        onClose={() => setIsZoom(false)}
        options={{
          arrowEl: true,
          captionEl: false,
          closeEl: true,
          closeOnScroll: false,
          counterEl: true,
          fullscreenEl: false,
          index: getActiveImage(),
          preloaderEl: false,
          shareEl: false,
          timeToIdle: isMobile ? 0 : 4000,
          zoomEl: false,
        }}
      />
    </StyledPhotoSwipe>
  );
};

export default GalleryZoomOverlay;
